<template>
  <div class="filter-builder">
    <div v-if="clauses.length === 0" class="filter-builder__empty text-muted small">
      No filters — segment will resolve to the whole list (active + subscribed only).
    </div>

    <div
      v-for="(clause, idx) in clauses"
      :key="idx"
      class="filter-builder__clause"
    >
      <span class="filter-builder__connector">
        <span v-if="idx === 0" class="text-muted small">WHERE</span>
        <span v-else class="badge bg-secondary">AND</span>
      </span>

      <select v-model="clause.field" class="form-select form-select-sm" @change="onFieldChange(clause)">
        <option v-for="f in FIELDS" :key="f.key" :value="f.key">{{ f.label }}</option>
      </select>

      <select v-model="clause.op" class="form-select form-select-sm" @change="onOpChange(clause)">
        <option v-for="o in opsFor(clause.field)" :key="o.key" :value="o.key">{{ o.label }}</option>
      </select>

      <!-- Value input morphs based on op type -->
      <template v-if="clause.op === 'in'">
        <input
          :value="(clause.value || []).join(', ')"
          class="form-control form-control-sm"
          placeholder="value1, value2, value3"
          @input="clause.value = $event.target.value.split(',').map(s => s.trim()).filter(Boolean); onChange();"
        />
      </template>
      <template v-else-if="clause.field === 'status'">
        <select v-model="clause.value" class="form-select form-select-sm" @change="onChange">
          <option v-for="s in STATUSES" :key="s" :value="s">{{ s }}</option>
        </select>
      </template>
      <template v-else-if="clause.field === 'tag'">
        <select v-model="clause.value" class="form-select form-select-sm" @change="onChange">
          <option v-if="!tagTerms.length" value="" disabled>No tags yet — add some on a subscriber</option>
          <option v-for="t in tagTerms" :key="t.key" :value="t.key">{{ t.label }}</option>
        </select>
      </template>
      <template v-else>
        <input
          v-model="clause.value"
          type="text"
          class="form-control form-control-sm"
          :placeholder="placeholderFor(clause.field, clause.op)"
          @input="onChange"
        />
      </template>

      <button
        type="button"
        class="btn btn-link btn-sm text-danger filter-builder__remove"
        title="Remove clause"
        @click="removeClause(idx)"
      >
        <CygnusIcon name="x" />
      </button>
    </div>

    <div class="filter-builder__toolbar">
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="addClause">
        <CygnusIcon name="plus" class="me-1" />Add clause
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        :disabled="!listId || previewing"
        @click="$emit('preview', { list_id: listId, filter_json: cleanClauses() })"
      >
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="previewing" />
        <CygnusIcon name="eye" class="me-1" v-else />
        Preview count
      </button>
      <span v-if="lastPreview != null" class="filter-builder__count">
        Matches: <strong>{{ lastPreview }}</strong> recipient<span v-if="lastPreview !== 1">s</span>
      </span>
    </div>
  </div>
</template>

<script>
/**
 * <SegmentFilterBuilder> — structured editor for the V1 filter DSL
 * shipped by api/module/segments/Service/SegmentResolverService.
 *
 * Model: v-model is an array of `{field, op, value}` objects, ANDed
 * together by the resolver. Empty array == "no filter" (whole list).
 *
 * Field whitelist + op support MUST stay in sync with the resolver's
 * `ALLOWED_FIELDS` constant + switch statement. Adding a new field /
 * op is two edits: this FIELDS / OPS_BY_TYPE table + the resolver.
 *
 * `$emit('preview', {list_id, filter_json})` lets parent wire to the
 * /admin/segments/preview-draft endpoint without this component
 * needing the api / list state.
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'SegmentFilterBuilder',
  props: {
    modelValue: { type: Array, default: () => [] },
    listId:     { type: [Number, null], default: null },
    previewing: { type: Boolean, default: false },
    lastPreview:{ type: [Number, null], default: null },
  },
  emits: ['update:modelValue', 'preview'],
  data() {
    return {
      api: useApi(),
      // Field whitelist mirrors SegmentResolverService::ALLOWED_FIELDS
      // (+ the synthetic `tag` field, resolved via entity_taxonomy_terms).
      FIELDS: [
        { key: 'status',     label: 'Status',     type: 'enum'  },
        { key: 'source',     label: 'Source',     type: 'text'  },
        { key: 'first_name', label: 'First name', type: 'text'  },
        { key: 'last_name',  label: 'Last name',  type: 'text'  },
        { key: 'email',      label: 'Email',      type: 'text'  },
        { key: 'tag',        label: 'Tag',        type: 'tag'   },
      ],
      // Mirrors subscribers.status enum
      STATUSES: ['pending', 'active', 'unsubscribed', 'bounced', 'complained'],
      // Op support per field type. Resolver supports =, !=, in, contains, has_tag.
      OPS_BY_TYPE: {
        enum: [{ key: '=', label: 'equals' }, { key: '!=', label: 'not equals' }, { key: 'in', label: 'is any of' }],
        text: [{ key: '=', label: 'equals' }, { key: '!=', label: 'not equals' }, { key: 'in', label: 'is any of' }, { key: 'contains', label: 'contains' }],
        tag:  [{ key: 'has_tag', label: 'has tag' }],
      },
      tagTerms: [], // [{key,label}] for the subscriber_tag value picker
      // Local clone so we can mutate without surprising parents
      clauses: [],
    };
  },
  mounted() {
    this.loadTagTerms();
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(v) {
        this.clauses = Array.isArray(v) ? JSON.parse(JSON.stringify(v)) : [];
      },
    },
  },
  methods: {
    async loadTagTerms() {
      try {
        const res = await this.api.get('/api/v1/taxonomies/subscriber_tag/terms');
        const payload = res?.data ?? res;
        this.tagTerms = (payload?.terms || []).map(t => ({ key: t.key, label: t.label }));
      } catch (e) { /* non-fatal — tag field just shows the empty-state option */ }
    },
    opsFor(fieldKey) {
      const f = this.FIELDS.find(x => x.key === fieldKey);
      return this.OPS_BY_TYPE[f?.type || 'text'];
    },
    onFieldChange(clause) {
      // Switching field can invalidate the op (e.g. tag only allows has_tag)
      // and the value shape. Reset both to safe defaults for the new field.
      const ops = this.opsFor(clause.field);
      clause.op = ops[0]?.key || '=';
      clause.value = clause.field === 'tag'
        ? (this.tagTerms[0]?.key || '')
        : (clause.field === 'status' ? 'active' : '');
      this.onChange();
    },
    placeholderFor(fieldKey, op) {
      if (fieldKey === 'source') return 'crawler:linkedin';
      if (fieldKey === 'email')  return op === 'contains' ? '@example.com' : 'jane@example.com';
      return '';
    },
    addClause() {
      this.clauses.push({ field: 'status', op: '=', value: 'active' });
      this.onChange();
    },
    removeClause(idx) {
      this.clauses.splice(idx, 1);
      this.onChange();
    },
    onChange() {
      this.$emit('update:modelValue', this.cleanClauses());
    },
    onOpChange(clause) {
      // 'in' takes an array; everything else takes a scalar — reset to
      // avoid sending the wrong shape to the resolver.
      if (clause.op === 'in' && !Array.isArray(clause.value)) {
        clause.value = clause.value ? [clause.value] : [];
      } else if (clause.op !== 'in' && Array.isArray(clause.value)) {
        clause.value = clause.value[0] || '';
      }
      this.onChange();
    },
    cleanClauses() {
      // Strip blank/null clauses before emitting so the resolver doesn't
      // see `{field: 'status', value: ''}` and silently skip them.
      return this.clauses.filter(c => {
        if (!c || !c.field) return false;
        if (c.op === 'in') return Array.isArray(c.value) && c.value.length > 0;
        return c.value !== '' && c.value != null;
      });
    },
  },
};
</script>

<style>
.filter-builder { display: flex; flex-direction: column; gap: 8px; }
.filter-builder__empty { padding: 8px 0; font-style: italic; }
.filter-builder__clause {
  display: grid;
  grid-template-columns: 60px 140px 130px 1fr 30px;
  gap: 8px;
  align-items: center;
}
.filter-builder__connector { text-align: right; }
.filter-builder__connector .badge { font-size: 0.65rem; padding: 3px 7px; }
.filter-builder__remove { padding: 0; line-height: 1; }
.filter-builder__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}
.filter-builder__count {
  font-size: 0.85rem;
  color: var(--m-text-secondary, #475569);
  margin-left: auto;
}
</style>
